import { NgClass } from '@angular/common';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { FormsModule, UntypedFormControl } from '@angular/forms';
import {
  NgbDateParserFormatter,
  NgbDateStruct,
  NgbDatepickerModule,
} from '@ng-bootstrap/ng-bootstrap';
import { DPFormField } from 'src/app/core/models/DPFormField';
import { DPValidationType } from 'src/app/core/models/enums/DPValidationType';
import { UtilsService } from 'src/app/core/services/utils.service';
import { NgbDateCustomParserFormatter } from './date-custom-parser-formatter.service';

/**
 * Date component.
 *
 * The component allows you to input date in mm-dd-yyyy format or select a date in dropdown.
 *
 * The value would be a string: mm-dd-yyyy
 */
@Component({
  selector: 'app-date',
  standalone: true,
  imports: [NgClass, FormsModule, NgbDatepickerModule],
  providers: [
    { provide: NgbDateParserFormatter, useClass: NgbDateCustomParserFormatter },
  ],
  templateUrl: './date.component.html',
  styleUrls: ['./date.component.scss'],
})
export class DateComponent implements OnInit, OnChanges {
  @Input() field: DPFormField;

  @Input() control: UntypedFormControl;

  @Input() disabled: boolean;

  // Minimum date
  @Input() min: string; // MM-DD-YYYY

  // Maximum date
  @Input() max: string; // MM-DD-YYYY

  // Current date object
  date: NgbDateStruct;

  // Minimum date object of NgbDate component
  minDate: NgbDateStruct | null = null;

  // Maximum date object of NgbDate component
  maxDate: NgbDateStruct | null = null;

  // If future dates allowed. Populated from field.validations
  isFutureDateNotAllowed: boolean;

  // If past dates allowed. Populated from field.validations
  isPastDateNotAllowed: boolean;

  // DateYearLimit value. Populated from field.validations
  rule13Value: number;

  // MinYearDateLimit value. Populated from field.validations
  rule14Value: number;

  constructor(private utils: UtilsService) {}

  ngOnInit(): void {
    this.patchValue();

    // On value change, update date value
    this.control?.valueChanges.subscribe((val: string) => {
      this.patchValue();
    });

    this.isFutureDateNotAllowed = !!this.field.validations?.find(
      (validation) =>
        validation.validationTypeId === DPValidationType.InvalidateFutureDate
    );
    this.isPastDateNotAllowed = !!this.field.validations?.find(
      (validation) =>
        validation.validationTypeId === DPValidationType.InvalidatePastDate
    );

    const rule13 = this.field.validations?.find(
      (validation) =>
        validation.validationTypeId === DPValidationType.DateYearLimit
    );
    this.rule13Value = rule13 && rule13.value ? +rule13.value : null;

    const rule14 = this.field.validations?.find(
      (validation) =>
        validation.validationTypeId === DPValidationType.MinDateYearLimit
    );
    this.rule14Value = rule14 && rule14.value ? +rule14.value : null;

    this.setMaxDate();
    this.setMinDate();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.max?.currentValue || changes.min?.currentValue) {
      this.setMaxDate();
      this.setMinDate();
    }
  }

  /**
   * When current date is updated in NgbDate component, update the form control value
   * so that it is saved in the parent form.
   * @param d Date object
   */
  updateDate(d: NgbDateStruct) {
    if (!d || typeof d === 'string') {
      return;
    }

    this.date = d;
    if (this.control) {
      const { year, month, day } = d;
      this.control.setValue(
        `${this.utils.pad2(month)}-${this.utils.pad2(day)}-${year}`
      );
      this.control.markAsTouched();
    }
  }

  isDigit(ch: string) {
    return ch.length === 1 && ch >= '0' && ch <= '9';
  }

  updateOnKeydown(ev: KeyboardEvent) {
    if (!ev.key) {
      return;
    }

    if (
      !(
        this.isDigit(ev.key) ||
        [
          'Backspace',
          'Delete',
          'Ctrl',
          'ArrowLeft',
          'ArrowRight',
          'Tab',
        ].includes(ev.key) ||
        (['c', 'v', 'z'].includes(ev.key) && ev.ctrlKey)
      )
    ) {
      ev.preventDefault();
    }

    if (this.isDigit(ev.key)) {
      ev.preventDefault();
      const target = ev.target as HTMLInputElement;
      let value = target.value;

      if (target.selectionStart !== target.selectionEnd) {
        value =
          value.slice(0, target.selectionStart) +
          ev.key +
          value.slice(target.selectionEnd);
      }

      let cursorPos = 0;
      let digits: string[] = [];
      for (let i = 0; i < value.length; i++) {
        const ch = value.charAt(i);
        if (this.isDigit(ch)) {
          if (i < target.selectionStart) {
            cursorPos++;
          }
          digits.push(ch);
        }
      }
      digits.splice(cursorPos, 1, ev.key);
      cursorPos++;
      if (digits.length > 1) {
        digits.splice(2, 0, '-');
        if (cursorPos > 1) {
          cursorPos++;
        }
      }
      if (digits.length > 4) {
        digits.splice(5, 0, '-');
        if (cursorPos > 4) {
          cursorPos++;
        }
      }

      value = digits.join('').slice(0, 10);

      target.value = value;
      cursorPos = Math.min(10, cursorPos);
      target.setSelectionRange(cursorPos, cursorPos);
    }
  }

  updateOnKeyUp(ev: Event) {
    const target = ev.target as HTMLInputElement;
    let value = target.value;

    // Update control value if input is changed
    if (this.control) {
      const d = this.utils.toDate(value);
      if (!value) {
        this.control.setValue('');
        this.control.markAllAsTouched();
        return;
      }

      if (isNaN(d?.getTime())) {
        this.control.setErrors({
          custom: 'Incorrect Date Format example:  mm-dd-yyyy',
        });
        return;
      }

      if (d?.getFullYear() < 1000) {
        this.control.setErrors({
          custom: 'Incorrect Date Format example:  mm-dd-yyyy',
        });
        return;
      }

      this.control.setErrors(null);
    }
  }

  /**
   * Update form control value when the input is changed.
   * @param ev
   */
  updateOnBlur(ev: Event) {
    const target = ev.target as HTMLInputElement;
    const d = this.utils.toDate(target.value);
    if (!target.value) {
      this.control?.setValue('');
      this.control?.markAllAsTouched();
      return;
    } else if (!d || isNaN(d.getTime())) {
      this.control?.setErrors({
        custom: 'Incorrect Date Format example:  mm-dd-yyyy',
      });
      return;
    }

    this.date = {
      year: d.getFullYear(),
      month: d.getMonth() + 1,
      day: d.getDate(),
    };
    if (this.control) {
      this.control.setValue(this.utils.getDateString(d));
      this.control.markAllAsTouched();
    }
  }

  /**
   * Populate date value from form control value.
   *
   * It also sets maximum and minimum dates for NgbDate component.
   */
  private patchValue() {
    if (!this.control || !this.control.value) {
      return;
    }

    this.date = this.utils.toNgbDateStruct(this.control.value);

    this.setMaxDate();
    this.setMinDate();

    if (!this.date) {
      this.control.setErrors({
        custom: 'Incorrect Date Format example: mm-dd-yyyy',
      });
      return;
    }

    if (this.date.year < 1000) {
      this.control.setErrors({
        custom: 'Incorrect Date Format example:  mm-dd-yyyy',
      });
    }
  }

  /**
   * Set minimum date for the Date Picker component.
   */
  private setMinDate() {
    const now = new Date();
    let minDate = new Date(
      now.getFullYear() - 100,
      now.getMonth(),
      now.getDate()
    ); // Min date

    /**
     * In some cases, the maxDate can be past date. (eg. When MinDateYearLimit is available)
     * In those cases, this condition makes sure the 100 years are selectable from maxDate.
     */
    if (this.maxDate && this.maxDate.year < now.getFullYear()) {
      minDate.setFullYear(this.maxDate.year - 100);
    }

    /**
     * This condition will make sure that
     * 100 years from selected date( or now if date is not selected yet)
     * is available in years dropdown.
     */
    if (this.date) {
      minDate.setFullYear(this.date.year - 100);
    }

    /**
     * This prevents selecting past date in calendar.
     */
    if (this.isPastDateNotAllowed && minDate < now) {
      minDate = now;
    }

    /**
     * This prevents selecting years out of DateYearLimit range.
     */
    if (this.rule13Value) {
      const newMin = new Date(
        now.getFullYear() - this.rule13Value,
        now.getMonth(),
        now.getDate()
      );
      if (minDate < newMin) {
        minDate = newMin;
      }
    }

    /**
     * This enforces min value from parent component.
     */
    if (this.min) {
      const newMin = this.utils.toDate(this.min);
      if (newMin) {
        minDate = newMin;
      }
    }

    this.minDate = this.utils.dateToNgbDateStruct(minDate);
    if (this.utils.compareDates1(this.minDate, this.maxDate) > 0) {
      this.maxDate = { ...this.minDate };
    }
  }

  /**
   * Set maximum date for the Date Picker component.
   */
  private setMaxDate() {
    const now = new Date();

    /**
     * Maximum date starts from now + 100 years by default.
     * This will be overriden later.
     */
    let maxDate = new Date(
      now.getFullYear() + 100,
      now.getMonth(),
      now.getDate()
    ); // Maximum Date

    /**
     * This condition will make sure that 100 years from selected date
     * is available in years dropdown.
     */
    if (this.date) {
      maxDate.setFullYear(this.date.year + 100);
    }

    /**
     * This prevents selecting future date in calendar.
     */
    if (this.isFutureDateNotAllowed && maxDate > now) {
      maxDate = now;
    }

    /**
     * This prevents selecting years out of DateYearLimit range.
     */
    if (this.rule13Value) {
      const newMax = new Date(
        now.getFullYear() + this.rule13Value,
        now.getMonth(),
        now.getDate()
      );
      if (maxDate > newMax) {
        maxDate = newMax;
      }
    }

    /**
     * This prevents selecting years after now - [MinDateYearLimit value]
     */
    if (this.rule14Value) {
      const newMax = new Date(
        now.getFullYear() - this.rule14Value,
        now.getMonth(),
        now.getDate()
      );
      if (maxDate > newMax) {
        maxDate = newMax;
      }
    }

    /**
     * This enforces min value from parent component.
     */
    if (this.max) {
      const newMax = this.utils.toDate(this.max);
      if (newMax) {
        maxDate = newMax;
      }
    }

    this.maxDate = this.utils.dateToNgbDateStruct(maxDate);
  }
}
